import type { FC } from 'react';

import { capitalizeFirstLetter } from '@eneco/common/string';
import { MonthCaptionProps, useDayPicker } from 'react-day-picker';

import { disabledButtonClass } from './MonthCaption.css';
import { YearsDropdown } from './YearsDropdown';
import { ChevronLeftIcon, ChevronRightIcon } from '../../../icons';
import { useI18nTranslations } from '../../../providers/i18n';
import { Box } from '../../Box/Box';
import { Divider } from '../../Divider/Divider';
import { Grid } from '../../Grid/Grid';
import { IconButton } from '../../IconButton/IconButton';
import { Stack } from '../../Stack/Stack';
import { Text } from '../../Text/Text';

const NextChevron: FC<{ date: Date }> = ({ date }) => {
  const { nextMonth: i18nNextMonth } = useI18nTranslations();
  const { goToMonth, months, nextMonth } = useDayPicker();

  // In cases where two months are displayed at a time (e.g. range mode), the right chevron should only be shown for
  //   the right most month
  if (months.length > 1 && months[months.length - 1].date.getMonth() !== date.getMonth())
    return <div className={disabledButtonClass} />;

  return nextMonth ? (
    <IconButton label={i18nNextMonth} onClick={() => goToMonth(nextMonth)}>
      <ChevronRightIcon />
    </IconButton>
  ) : (
    <div className={disabledButtonClass}>
      <ChevronRightIcon />
    </div>
  );
};

const PreviousChevron: FC<{ date: Date }> = ({ date }) => {
  const { previousMonth: i18nPreviousMonth } = useI18nTranslations();
  const { goToMonth, months, previousMonth } = useDayPicker();

  // In cases where two months are displayed at a time (e.g. range mode), the left chevron should only be shown for
  //   the left most month
  if (months.length > 1 && months[0].date.getMonth() !== date.getMonth())
    return <div className={disabledButtonClass} />;

  return previousMonth ? (
    <IconButton label={i18nPreviousMonth} onClick={() => goToMonth(previousMonth)}>
      <ChevronLeftIcon />
    </IconButton>
  ) : (
    <div className={disabledButtonClass}>
      <ChevronLeftIcon />
    </div>
  );
};

export const MonthCaption = (props: MonthCaptionProps) => {
  const {
    dayPickerProps: { locale },
    months,
  } = useDayPicker();

  const {
    calendarMonth: { date },
    className,
    displayIndex, // stripped out (not a valid dom attr)
    ...monthCaptionProps
  } = props;

  return (
    <div className={className} {...monthCaptionProps}>
      <Box padding="6">
        <Grid gridTemplateColumns="1fr max-content">
          <Grid.Item>
            <Stack gap="1" direction="row" alignY="center" alignX="justify">
              <PreviousChevron date={date} />
              <Box role="status">
                <Text size="BodyL" weight="bold">
                  {capitalizeFirstLetter(date.toLocaleString(locale?.code, { month: 'long' }))}
                  {months.length > 1 ? ` ${date.getFullYear()}` : ''}
                </Text>
              </Box>
              <NextChevron date={date} />
            </Stack>
          </Grid.Item>
          <Grid.Item alignSelf="end">
            <YearsDropdown date={date} />
          </Grid.Item>
        </Grid>
      </Box>
      <Divider />
    </div>
  );
};
